import { useEffect, useRef } from 'react'
import { X, Zap, Check, Minus } from 'lucide-react'
import { getDisplayName, getInformalName, getTeamLogo, formatOdds } from '@/lib/teams'

/** User's history with this specific team */
interface TeamHistory {
  picks: number
  wins: number
  draws: number
  losses: number
  pointsEarned: number
}

/** Team's EPL season record */
interface SeasonRecord {
  wins: number
  draws: number
  losses: number
  points: number
  position: number
}

type PickResult = 'win' | 'draw' | 'loss'

interface TeamInfoPopoverProps {
  teamName: string
  opponentName: string
  teams?: { name: string; logo: string; code?: string }[]
  isHome: boolean
  kickoff: string
  homeOdds: number | undefined
  drawOdds: number | undefined
  awayOdds: number | undefined
  usage: number
  tddEligible: boolean
  seasonRecord?: SeasonRecord
  teamHistory?: TeamHistory
  onClose: () => void
}

function ResultIcon({ result }: { result: PickResult }) {
  if (result === 'win') return <Check className="w-3.5 h-3.5 text-emerald-500" />
  if (result === 'loss') return <X className="w-3.5 h-3.5 text-red-500" />
  return <Minus className="w-3.5 h-3.5 text-zinc-400" />
}

function formatKickoffFull(kickoff: string): string {
  const date = new Date(kickoff)
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function summarizeResults(wins: number, draws: number, losses: number): string {
  const parts: string[] = []
  if (wins > 0) parts.push(`${wins}W`)
  if (draws > 0) parts.push(`${draws}D`)
  if (losses > 0) parts.push(`${losses}L`)
  return parts.join(', ')
}

export function TeamInfoPopover({
  teamName,
  opponentName,
  teams,
  isHome,
  kickoff,
  homeOdds,
  drawOdds,
  awayOdds,
  usage,
  tddEligible,
  seasonRecord,
  teamHistory,
  onClose,
}: TeamInfoPopoverProps) {
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const displayName = getDisplayName(teamName)
  const informalName = getInformalName(teamName)
  const opponentDisplay = getInformalName(opponentName)
  const logo = getTeamLogo(teamName, teams)
  const teamOdds = isHome ? homeOdds : awayOdds
  const oppOdds = isHome ? awayOdds : homeOdds

  // Get team code for the badge (e.g., "BR" for Brighton)
  const teamMatch = teams?.find(t => t.name === teamName || getDisplayName(t.name) === displayName)
  const teamCode = teamMatch?.code || informalName.slice(0, 2).toUpperCase()

  // Calculate points breakdown from history
  const historyWins = teamHistory?.wins ?? 0
  const historyDraws = teamHistory?.draws ?? 0
  const historyLosses = teamHistory?.losses ?? 0
  const historyPoints = teamHistory?.pointsEarned ?? 0
  const hasPicked = usage > 0
  const resultSummary = summarizeResults(historyWins, historyDraws, historyLosses)

  // Build results array for badges
  const results: PickResult[] = [
    ...Array(historyWins).fill('win'),
    ...Array(historyDraws).fill('draw'),
    ...Array(historyLosses).fill('loss'),
  ] as PickResult[]

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-16 sm:py-8"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose()
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />

      {/* Popover Card */}
      <div className="relative w-full max-w-sm max-h-full overflow-y-auto bg-white dark:bg-zinc-900 rounded-2xl
                      border border-zinc-200 dark:border-zinc-700 shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center overflow-hidden">
              {logo.startsWith('http') ? (
                <img src={logo} alt="" className="w-8 h-8 object-contain" />
              ) : (
                <span className="text-sm font-bold text-zinc-400 dark:text-zinc-500">{teamCode}</span>
              )}
            </div>
            <div>
              <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{informalName}</h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">{teamName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 -m-2 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100
                       dark:text-zinc-500 dark:hover:text-zinc-300 dark:hover:bg-zinc-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="h-px bg-zinc-200 dark:bg-zinc-700 mx-5" />

        {/* Season Stats */}
        <div className="px-5 py-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500">Season</span>
            {seasonRecord?.position && (
              <span className="text-xs font-mono font-semibold text-zinc-900 dark:text-zinc-100">
                #{seasonRecord.position}
              </span>
            )}
          </div>
          {seasonRecord && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-600 dark:text-zinc-400">Record</span>
              <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                {seasonRecord.wins}W – {seasonRecord.draws}D – {seasonRecord.losses}L
                <span className="text-zinc-500 dark:text-zinc-400 ml-1">({seasonRecord.points} pts)</span>
              </span>
            </div>
          )}
        </div>

        <div className="h-px bg-zinc-200 dark:bg-zinc-700 mx-5" />

        {/* Your History */}
        <div className="px-5 py-4 space-y-2">
          <span className="text-xs font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
            Your History
          </span>
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-600 dark:text-zinc-400">Picked</span>
            <span className={`text-sm font-medium ${usage >= 2 ? 'text-red-600 dark:text-red-400' : 'text-zinc-900 dark:text-zinc-100'}`}>
              {usage}/2 times
            </span>
          </div>
          {hasPicked && results.length > 0 ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-400">Points earned</span>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-semibold ${historyPoints > 0 ? 'text-emerald-600 dark:text-emerald-400' : historyPoints < 0 ? 'text-red-600 dark:text-red-400' : 'text-zinc-600 dark:text-zinc-400'}`}>
                    {historyPoints > 0 ? '+' : ''}{historyPoints} pts
                  </span>
                  {resultSummary && (
                    <span className="text-xs text-zinc-400 dark:text-zinc-500">({resultSummary})</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                {results.map((result, i) => (
                  <div key={i} className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800">
                    <ResultIcon result={result} />
                    <span className="text-xs capitalize text-zinc-600 dark:text-zinc-400">{result}</span>
                  </div>
                ))}
              </div>
            </>
          ) : hasPicked ? (
            <p className="text-sm text-zinc-400 dark:text-zinc-500 italic">Results pending</p>
          ) : null}
          {tddEligible && (
            <div className="flex items-center gap-1.5 mt-1">
              <Zap className="w-3.5 h-3.5 text-violet-500" />
              <span className="text-sm font-medium text-violet-600 dark:text-violet-400">DD eligible</span>
            </div>
          )}
        </div>

        <div className="h-px bg-zinc-200 dark:bg-zinc-700 mx-5" />

        {/* This Match */}
        <div className="px-5 py-4 pb-5 space-y-2">
          <span className="text-xs font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
            This Match
          </span>
          <p className="text-sm text-zinc-900 dark:text-zinc-100">
            {isHome ? 'Home' : 'Away'} vs {opponentDisplay} – {formatKickoffFull(kickoff)}
          </p>
          <div className="flex items-center gap-2 text-xs">
            {teamOdds != null && (
              <span className={`px-2 py-1 rounded font-medium
                ${isHome
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400'
                  : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                }`}>
                {informalName} <span className="font-mono font-bold">{formatOdds(teamOdds)}</span>
              </span>
            )}
            {drawOdds != null && (
              <span className="px-2 py-1 rounded font-medium bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
                Draw <span className="font-mono font-bold">{formatOdds(drawOdds)}</span>
              </span>
            )}
            {oppOdds != null && (
              <span className={`px-2 py-1 rounded font-medium
                ${!isHome
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400'
                  : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                }`}>
                {opponentDisplay} <span className="font-mono font-bold">{formatOdds(oppOdds)}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
